import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { environment } from '@environments/environment';
import { catchError, Observable, throwError } from 'rxjs';
import { ApiResponse } from '@core/types';
import {
  Unit,
  UnitDetail,
  UnitCreate,
  UnitUpdate,
  UnitFilter,
  Resident,
  ResidentCreate,
  ResidentUpdate,
  ZoneOption,
  UnitBulkImportPayload,
  BulkImportResult,
} from '@core/types/unit.type';

export interface PaginationResult<T> {
  list: T[];
  totalRecord: number;
  pageCount: number;
}

export interface PaginationConfig {
  pageNumber: number;
  pageSize: number;
  searchTerm?: string;
  filter?: unknown;
  sortField?: string;
  sortOrder?: 1 | -1 | null;
}

@Injectable({
  providedIn: 'root',
})
export class UnitService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.api}/units`;

  // Unit CRUD operations

  /**
   * Get paginated list of units with filters
   */
  getAll(config: PaginationConfig): Observable<ApiResponse<PaginationResult<Unit>>> {
    let params = new HttpParams()
      .set('pageNumber', config.pageNumber.toString())
      .set('pageSize', config.pageSize.toString());

    if (config.searchTerm) {
      params = params.set('searchTerm', config.searchTerm);
    }

    if (config.filter?.zoneId) {
      params = params.set('filter.zoneId', config.filter.zoneId);
    }

    if (config.filter?.useType !== undefined) {
      params = params.set('filter.useType', config.filter.useType.toString());
    }

    if (config.filter?.hasResident !== undefined) {
      params = params.set('filter.hasResident', config.filter.hasResident.toString());
    }

    return this.http.get<ApiResponse<PaginationResult<Unit>>>(`${this.baseUrl}/grid`, { params })
      .pipe(
        catchError((errorResponse: HttpErrorResponse) => {
          return throwError(() => errorResponse.error?.message || 'Error loading units');
        })
      );
  }

  /**
   * Get unit by ID with residents
   */
  getById(id: string): Observable<ApiResponse<UnitDetail>> {
    return this.http.get<ApiResponse<UnitDetail>>(`${this.baseUrl}/${id}`)
      .pipe(
        catchError((errorResponse: HttpErrorResponse) => {
          return throwError(() => errorResponse.error?.message || 'Error loading unit');
        })
      );
  }

  /**
   * Create a new unit
   */
  create(unit: UnitCreate): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(this.baseUrl, unit)
      .pipe(
        catchError((errorResponse: HttpErrorResponse) => {
          return throwError(() => errorResponse.error?.message || 'Error creating unit');
        })
      );
  }

  /**
   * Update an existing unit
   */
  update(unit: UnitUpdate): Observable<ApiResponse<void>> {
    return this.http.put<ApiResponse<void>>(this.baseUrl, unit)
      .pipe(
        catchError((errorResponse: HttpErrorResponse) => {
          return throwError(() => errorResponse.error?.message || 'Error updating unit');
        })
      );
  }

  /**
   * Delete a unit (soft delete)
   */
  delete(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.baseUrl}/${id}`)
      .pipe(
        catchError((errorResponse: HttpErrorResponse) => {
          return throwError(() => errorResponse.error?.message || 'Error deleting unit');
        })
      );
  }

  /**
   * Bulk update maintenance fees
   */
  bulkUpdateMaintenanceFee(data: any): Observable<ApiResponse<void>> {
    return this.http.put<ApiResponse<void>>(`${this.baseUrl}/maintenance-fee`, data)
      .pipe(
        catchError((errorResponse: HttpErrorResponse) => {
          return throwError(() => errorResponse.error?.message || 'Error updating maintenance fees');
        })
      );
  }

  // Resident operations

  /**
   * Get active residents for a unit
   */
  getResidents(unitId: string): Observable<ApiResponse<Resident[]>> {
    return this.http.get<ApiResponse<Resident[]>>(`${this.baseUrl}/${unitId}/residents`)
      .pipe(
        catchError((errorResponse: HttpErrorResponse) => {
          return throwError(() => errorResponse.error?.message || 'Error loading residents');
        })
      );
  }

  /**
   * Get full resident history for a unit
   */
  getResidentHistory(unitId: string): Observable<ApiResponse<Resident[]>> {
    return this.http.get<ApiResponse<Resident[]>>(`${this.baseUrl}/${unitId}/residents/history`)
      .pipe(
        catchError((errorResponse: HttpErrorResponse) => {
          return throwError(() => errorResponse.error?.message || 'Error loading resident history');
        })
      );
  }

  /**
   * Add a new resident to a unit
   */
  addResident(unitId: string, resident: Omit<ResidentCreate, 'unitId'>): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.baseUrl}/${unitId}/residents`, resident)
      .pipe(
        catchError((errorResponse: HttpErrorResponse) => {
          return throwError(() => errorResponse.error?.message || 'Error adding resident');
        })
      );
  }

  /**
   * Update a resident
   */
  updateResident(unitId: string, residentId: string, resident: Omit<ResidentUpdate, 'unitId' | 'id'>): Observable<ApiResponse<void>> {
    return this.http.put<ApiResponse<void>>(`${this.baseUrl}/${unitId}/residents/${residentId}`, resident)
      .pipe(
        catchError((errorResponse: HttpErrorResponse) => {
          return throwError(() => errorResponse.error?.message || 'Error updating resident');
        })
      );
  }

  /**
   * Remove a resident from a unit
   */
  removeResident(unitId: string, residentId: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.baseUrl}/${unitId}/residents/${residentId}`)
      .pipe(
        catchError((errorResponse: HttpErrorResponse) => {
          return throwError(() => errorResponse.error?.message || 'Error removing resident');
        })
      );
  }

  /**
   * Bulk import units from an Excel file payload
   */
  bulkImport(payload: UnitBulkImportPayload): Observable<ApiResponse<BulkImportResult>> {
    return this.http.post<ApiResponse<BulkImportResult>>(`${this.baseUrl}/bulk-import`, payload)
      .pipe(
        catchError((errorResponse: HttpErrorResponse) => {
          return throwError(() => errorResponse.error?.message || 'Error importing units');
        })
      );
  }

  // Helper method to get zones for dropdown (uses existing condominium detail)
  getZones(): Observable<ApiResponse<ZoneOption[]>> {
    // This would typically come from a dropdown service
    // For now, we'll use the condominium service to get zones
    return this.http.get<ApiResponse<ZoneOption[]>>(`${environment.api}/dropdown/zones`)
      .pipe(
        catchError((errorResponse: HttpErrorResponse) => {
          return throwError(() => errorResponse.error?.message || 'Error loading zones');
        })
      );
  }
}
